import { useCallback, useEffect, useState } from 'react';
import { markMessagesRead } from '../services/api';

const READ_STATE_EVENT = 'ae:message-read-state';
export const makeThreadKey = (contact) => `${contact.participant_id}:${contact.student_id}`;
export const makeThreadVersion = (contact) => JSON.stringify([
  contact.latest_sent_at,
  contact.latest_sender_id,
  contact.message_count,
]);

function readStored(key, fallback) {
  try {
    const persistent = localStorage.getItem(key);
    const legacy = persistent === null ? sessionStorage.getItem(key) : null;
    const value = JSON.parse(persistent || legacy || JSON.stringify(fallback));
    if (!value || typeof value !== 'object' || Array.isArray(value) !== Array.isArray(fallback)) {
      throw new Error('Invalid saved message state.');
    }
    if (legacy !== null) {
      localStorage.setItem(key, JSON.stringify(value));
      sessionStorage.removeItem(key);
    }
    return value;
  } catch (error: any) {
    console.warn('Could not restore message state:', error);
    return fallback;
  }
}

function saveStored(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    window.dispatchEvent(new Event(READ_STATE_EVENT));
  } catch (error: any) {
    console.warn('Could not save message state:', error);
  }
}

export function useMessageReadState(user) {
  const userId = user?.user_id ?? user?.id;
  const versionsKey = `ae_read_thread_versions:${userId}`;
  const messagesKey = `ae_read_messages:${userId}`;
  const trackedThreadsKey = `ae_read_message_threads:${userId}`;
  const dismissedKey = `ae_dismissed_messages:${userId}`;
  const [, setRevision] = useState<any>(0);

  useEffect(() => {
    const refresh = () => setRevision((value) => value + 1);
    window.addEventListener(READ_STATE_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(READ_STATE_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const versions = readStored(versionsKey, {});
  const readIds = readStored(messagesKey, []);
  const trackedThreads = readStored(trackedThreadsKey, []);
  const dismissedIds = readStored(dismissedKey, []);

  const markThreadRead = useCallback(async (contact, messages) => {
    const received = messages.filter((message) => !message.is_mine && message.message_id != null);
    const ids = received.map((message) => message.message_id);
    const unsavedIds = received.filter((message) => message.is_read !== true)
      .map((message) => message.message_id);
    // The chat endpoint saves receipts while returning the messages; retry only for older responses.
    for (let offset = 0; offset < unsavedIds.length; offset += 1000) {
      await markMessagesRead(unsavedIds.slice(offset, offset + 1000));
    }
    saveStored(messagesKey, [...new Set([...readStored(messagesKey, []), ...ids])]);
    saveStored(trackedThreadsKey, [...new Set([
      ...readStored(trackedThreadsKey, []), makeThreadKey(contact),
    ])]);
    saveStored(versionsKey, {
      ...readStored(versionsKey, {}),
      [makeThreadKey(contact)]: makeThreadVersion(contact),
    });
  }, [versionsKey, messagesKey, trackedThreadsKey]);

  const dismissMessage = useCallback((messageId) => {
    saveStored(dismissedKey, [...new Set([...readStored(dismissedKey, []), messageId])]);
  }, [dismissedKey]);

  const isMessageUnread = (message) => {
    if (message.is_read === true) return false;
    if (readIds.includes(message.message_id)) return false;
    // Honor conversation versions saved before message-level read tracking was added.
    const threadKey = `${message.sender_id}:${message.student_id || 0}`;
    if (trackedThreads.includes(threadKey)) return true;
    const savedVersion = versions[threadKey];
    if (savedVersion && message.sent_at) {
      try {
        const [readAt] = JSON.parse(savedVersion);
        if (readAt && new Date(message.sent_at).getTime() <= new Date(readAt).getTime()) {
          return false;
        }
      } catch (error: any) {
        console.warn('Could not restore conversation version:', error);
      }
    }
    return true;
  };

  return { versions, dismissedIds, markThreadRead, dismissMessage, isMessageUnread };
}

export function useUnreadMessagingSummary(summary, user) {
  const { isMessageUnread } = useMessageReadState(user);
  if (!summary) return null;
  const incoming = summary.incoming_messages
    || summary.recent_messages
    || (summary.latest_message ? [summary.latest_message] : []);
  const unread = incoming.filter(isMessageUnread);
  return {
    ...summary,
    incoming_count: unread.length,
    incoming_messages: unread,
    recent_messages: unread.slice(0, 10),
    latest_message: unread[0] || null,
  };
}
